import ExcelJS from 'exceljs'
import { supabase } from '../supabaseClient'
import { autoSizeColumns, downloadBlob, styleHeaderRow, workbookToBlob } from './shared'

export interface ExportGuest {
  slot_number: number
  full_name: string | null
  mobile: string | null
  email: string | null
}

export interface ExportRow {
  company_id: string
  company_code: string
  company_name: string
  hotel_id: string
  hotel_name: string
  room_type_id: string
  room_type_name: string
  room_id: string
  room_sequence: number
  display_label: string
  physical_room_number: string | null
  capacity: number
  room_check_in: string | null
  room_check_out: string | null
  room_board_type: string | null
  nights: number | null
  room_notes: string | null
  guests: ExportGuest[] | null
  guests_filled: number
  portal_status: string
  deadline_at: string | null
  submission_status: string
  last_activity_at: string | null
}

export interface ExportFilters {
  eventId: string
  companyId?: string | null
  hotelId?: string | null
  roomTypeId?: string | null
  portalStatus?: string | null
}

export async function fetchRows(filters: ExportFilters): Promise<ExportRow[]> {
  const { data, error } = await supabase.rpc('admin_export_rows', {
    p_event_id: filters.eventId,
    p_company_id: filters.companyId || null,
    p_hotel_id: filters.hotelId || null,
    p_room_type_id: filters.roomTypeId || null,
    p_portal_status: filters.portalStatus || null,
  })
  if (error) throw error
  return (data as ExportRow[]) || []
}

/**
 * Builds the full multi-sheet export workbook: ROOMING LIST (one row per
 * room), GUESTS (one row per guest), SUMMARY (per hotel/room-type roll-up +
 * event totals) and COMPANIES (per-company progress) -- sections 39-42.
 */
export async function buildRoomingListExport(filters: ExportFilters, eventName: string, fileLabel = 'Rooming_List'): Promise<void> {
  const rows = await fetchRows(filters)
  const wb = new ExcelJS.Workbook()
  wb.creator = 'Rooming List'
  wb.created = new Date()

  const maxCapacity = rows.reduce((m, r) => Math.max(m, r.capacity), 1)

  // --- ROOMING LIST: one row per room ---
  const roomingSheet = wb.addWorksheet('ROOMING LIST')
  const guestCols: string[] = []
  for (let i = 1; i <= maxCapacity; i++) guestCols.push(`Guest ${i} Name`, `Guest ${i} Mobile`)
  const roomingHeaders = ['Company', 'Hotel', 'Room Type', 'Room Slot', 'Physical Room Number', 'Capacity', ...guestCols, 'Check-in', 'Check-out', 'Nights', 'Board Type', 'Room Notes']
  roomingSheet.addRow(roomingHeaders)
  styleHeaderRow(roomingSheet.getRow(1))
  roomingSheet.views = [{ state: 'frozen', ySplit: 1 }]
  roomingSheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: roomingHeaders.length } }

  for (const r of rows) {
    const guestCells: (string | number)[] = []
    for (let i = 1; i <= maxCapacity; i++) {
      const g = (r.guests || []).find((x) => x.slot_number === i)
      guestCells.push(g?.full_name || '', g?.mobile || '')
    }
    roomingSheet.addRow([
      r.company_name,
      r.hotel_name,
      r.room_type_name,
      r.display_label,
      r.physical_room_number || '',
      r.capacity,
      ...guestCells,
      r.room_check_in || '',
      r.room_check_out || '',
      r.nights ?? '',
      r.room_board_type || '',
      r.room_notes || '',
    ])
  }
  autoSizeColumns(roomingSheet)

  // --- GUESTS: one row per guest ---
  const guestSheet = wb.addWorksheet('GUESTS')
  guestSheet.addRow(['Company', 'Hotel', 'Room Type', 'Room Slot', 'Physical Room Number', 'Guest Slot', 'Guest Name', 'Mobile', 'Email', 'Check-in', 'Check-out', 'Nights', 'Board Type', 'Notes'])
  styleHeaderRow(guestSheet.getRow(1))
  guestSheet.views = [{ state: 'frozen', ySplit: 1 }]
  for (const r of rows) {
    for (const g of r.guests || []) {
      if (!g.full_name) continue
      guestSheet.addRow([
        r.company_name,
        r.hotel_name,
        r.room_type_name,
        r.display_label,
        r.physical_room_number || '',
        g.slot_number,
        g.full_name,
        g.mobile || '',
        g.email || '',
        r.room_check_in || '',
        r.room_check_out || '',
        r.nights ?? '',
        r.room_board_type || '',
        '',
      ])
    }
  }
  autoSizeColumns(guestSheet)

  // --- SUMMARY: per hotel/room-type roll-up ---
  const summarySheet = wb.addWorksheet('SUMMARY')
  summarySheet.addRow(['Company', 'Hotel', 'Room Type', 'Allocated Rooms', 'Occupied Rooms', 'Capacity', 'Guests', 'Remaining Slots', 'Total Nights'])
  styleHeaderRow(summarySheet.getRow(1))
  const groups = new Map<string, { company: string; hotel: string; roomType: string; rooms: number; occupied: number; capacity: number; guests: number; nights: number }>()
  for (const r of rows) {
    const key = `${r.company_id}|${r.hotel_id}|${r.room_type_id}`
    const g = groups.get(key) || { company: r.company_name, hotel: r.hotel_name, roomType: r.room_type_name, rooms: 0, occupied: 0, capacity: 0, guests: 0, nights: 0 }
    g.rooms += 1
    g.capacity += r.capacity
    g.guests += r.guests_filled
    g.nights += r.nights || 0
    if (r.guests_filled > 0) g.occupied += 1
    groups.set(key, g)
  }
  let totalRooms = 0,
    totalCapacity = 0,
    totalGuests = 0,
    totalNightsSum = 0
  for (const g of groups.values()) {
    summarySheet.addRow([g.company, g.hotel, g.roomType, g.rooms, g.occupied, g.capacity, g.guests, g.capacity - g.guests, g.nights])
    totalRooms += g.rooms
    totalCapacity += g.capacity
    totalGuests += g.guests
    totalNightsSum += g.nights
  }
  summarySheet.addRow([])
  const totalsRow = summarySheet.addRow(['TOTAL', '', '', totalRooms, '', totalCapacity, totalGuests, totalCapacity - totalGuests, totalNightsSum])
  totalsRow.font = { bold: true }
  autoSizeColumns(summarySheet)

  // --- COMPANIES: per-company progress ---
  const companySheet = wb.addWorksheet('COMPANIES')
  companySheet.addRow(['Company', 'Allocated Rooms', 'Guest Capacity', 'Guests Entered', 'Remaining Slots', 'Deadline', 'Status', 'Last Updated'])
  styleHeaderRow(companySheet.getRow(1))
  const byCompany = new Map<string, { name: string; rooms: number; capacity: number; guests: number; deadline: string | null; status: string; lastUpdate: string | null }>()
  for (const r of rows) {
    const c = byCompany.get(r.company_id) || { name: r.company_name, rooms: 0, capacity: 0, guests: 0, deadline: r.deadline_at, status: r.portal_status, lastUpdate: r.last_activity_at }
    c.rooms += 1
    c.capacity += r.capacity
    c.guests += r.guests_filled
    byCompany.set(r.company_id, c)
  }
  for (const c of byCompany.values()) {
    companySheet.addRow([c.name, c.rooms, c.capacity, c.guests, c.capacity - c.guests, c.deadline ? new Date(c.deadline) : '', c.status, c.lastUpdate ? new Date(c.lastUpdate) : ''])
  }
  autoSizeColumns(companySheet)

  const blob = await workbookToBlob(wb)
  downloadBlob(blob, `${eventName.replace(/[^A-Za-z0-9]+/g, '_')}_${fileLabel}.xlsx`)
}

export async function buildHotelExport(filters: ExportFilters, hotelName: string, eventName: string): Promise<void> {
  await buildRoomingListExport(filters, eventName, `${hotelName.replace(/[^A-Za-z0-9]+/g, '_')}_Final_Rooming_List`)
}

export async function buildCompanyExport(filters: ExportFilters, companyName: string, eventName: string): Promise<void> {
  await buildRoomingListExport(filters, eventName, `${companyName.replace(/[^A-Za-z0-9]+/g, '_')}`)
}

/**
 * Bulk "send everyone their link" sheet (section 87) -- one row per company
 * with its portal URL, status and deadline, ready to mail-merge or hand to
 * an events coordinator without opening each company individually.
 */
export async function buildLinksExport(eventId: string, eventName: string): Promise<void> {
  const { data, error } = await supabase
    .from('companies')
    .select('code, name, contact_person, contact_mobile, contact_email, submission_status, company_portals(portal_token, status, deadline_at)')
    .eq('event_id', eventId)
    .is('archived_at', null)
    .order('name')
  if (error) throw error

  const wb = new ExcelJS.Workbook()
  const sheet = wb.addWorksheet('Company Links')
  sheet.addRow(['Company Code', 'Company Name', 'Contact Person', 'Contact Mobile', 'Contact Email', 'Portal Link', 'Portal Status', 'Deadline', 'Submission Status'])
  styleHeaderRow(sheet.getRow(1))
  sheet.views = [{ state: 'frozen', ySplit: 1 }]

  const { companyPortalUrl } = await import('../env')
  for (const c of (data as any[]) || []) {
    const portal = Array.isArray(c.company_portals) ? c.company_portals[0] : c.company_portals
    sheet.addRow([
      c.code,
      c.name,
      c.contact_person || '',
      c.contact_mobile || '',
      c.contact_email || '',
      portal?.portal_token ? companyPortalUrl(portal.portal_token) : '',
      portal?.status || '',
      portal?.deadline_at ? new Date(portal.deadline_at) : '',
      c.submission_status,
    ])
  }
  autoSizeColumns(sheet)

  const blob = await workbookToBlob(wb)
  downloadBlob(blob, `${eventName.replace(/[^A-Za-z0-9]+/g, '_')}_Company_Links.xlsx`)
}
